/**
 * Normalise a certifications value from the database.
 * Handles three formats:
 *  1. New JSONB objects — { name, description, url, image_url }
 *  2. Stringified JSON (TEXT[] column storing objects) — '{"name":"…"}'
 *  3. Old plain strings — "CFEI - Certified Fire & Explosion Investigator"
 *
 * Each certification object has:
 *  - name        (string, required)
 *  - description (string)
 *  - url         (string)
 *  - image_url   (string)
 */
export const normaliseCertifications = (raw) => {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((item) => {
    // Already a proper object
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      return {
        name: item.name || '',
        description: item.description || '',
        url: item.url || '',
        image_url: item.image_url || '',
      };
    }

    // String — might be stringified JSON or a plain cert name
    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object' && parsed.name) {
            return {
              name: parsed.name || '',
              description: parsed.description || '',
              url: parsed.url || '',
              image_url: parsed.image_url || '',
            };
          }
        } catch {
          // Not valid JSON — fall through to plain string handling
        }
      }
      return { name: trimmed, description: '', url: '', image_url: '' };
    }

    return { name: String(item ?? ''), description: '', url: '', image_url: '' };
  });
};

/** Return just the name string from a (possibly mixed) certification entry. */
export const certName = (c) => {
  if (typeof c === 'string') {
    const trimmed = c.trim();
    if (trimmed.startsWith('{')) {
      try { return JSON.parse(trimmed).name || trimmed; } catch { /* ignore */ }
    }
    return trimmed;
  }
  return c?.name || '';
};

/** Ensure a URL has a protocol so it isn't treated as a relative path. */
export const ensureAbsoluteUrl = (url) => {
  if (!url) return url;
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};
